import React, {useState, useMemo, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect, useSelector, useDispatch} from 'react-redux';
import _ from 'lodash';

import LabwareList from './LabwareList';
import LabwareItem from './LabwareItem';

import {DragDropType} from '../../../constants/dataEnum';

import {draggable} from '../../../lib/dragAndDrop';

import {filter, findAndExcludeObjectValueByKeys} from '../../../lib/objectHelper';
import LabwareSearch from './LabwareSearch';
import withIntl from '../../../lib/withIntl';
import {getFavoriteLabwareIds} from '../../../stores/selectors/userPreferenceSelector';
import {
  addAndUpdateFavoriteLabware,
  removeAndUpdateFavoriteLabware,
} from '../../../stores/business/userPreferenceBusiness';
import FastContextMenu from '../../../components/FastContextMenu/FastContextMenu.jsx';

const DraggableLabware = draggable(LabwareItem);

function mapStateToProps(state) {
  return {
    availableLabwareState: state.availableLabwareState,
    deepWellThreshold: state.configState.deepWellThreshold,
  };
}

function LabwareListContainer({
  intl,
  availableLabwareState,
  deepWellThreshold,
  filterParameter,
  setDraggedLabwareId,
  setDraggedLabwareType,
}) {
  const dispatch = useDispatch();
  const favoriteLabwareIds = useSelector(getFavoriteLabwareIds);

  const [filterCondition, setFilterCondition] = useState(filterParameter);
  const [selectedLabware, setSelectedLabware] = useState();

  const labwareRefs = useRef([]);

  const labwareList = useMemo(() => {
    const filteredLabware = filter(availableLabwareState, filterCondition);
    const {found, excluded} = findAndExcludeObjectValueByKeys(filteredLabware, favoriteLabwareIds);

    return [...found.map((lab) => ({...lab, isFavorite: true})), ...Object.values(excluded)];
  }, [filterCondition, availableLabwareState, favoriteLabwareIds]);

  const onFavoriteMenuClick = async () => {
    if (selectedLabware.isFavorite) {
      dispatch(removeAndUpdateFavoriteLabware(selectedLabware.LabwareID));
    } else {
      dispatch(addAndUpdateFavoriteLabware(selectedLabware.LabwareID));
    }
  };

  const getContextMenuLabel = useCallback(
    () =>
      selectedLabware && selectedLabware?.isFavorite
        ? intl.formatMessage({id: 'protocolEditor.labwareList.contextMenu.removeFromFavorite'})
        : intl.formatMessage({id: 'protocolEditor.labwareList.contextMenu.addToFavorite'}),
    [selectedLabware, intl],
  );

  const getContextMenuIcon = useCallback(
    () => (selectedLabware && selectedLabware?.isFavorite ? 'StarOff' : 'StartFilled'),
    [selectedLabware],
  );

  const onContextMenuClicked = useCallback(
    (target, index) => {
      setSelectedLabware(labwareList[Number(index)]);
    },
    [labwareList],
  );

  return (
    <LabwareList>
      <FastContextMenu
        elementsRef={labwareRefs}
        onContextMenu={onContextMenuClicked}
        contextMenuItems={[
          {
            label: getContextMenuLabel(),
            icon: getContextMenuIcon(),
            onClick: onFavoriteMenuClick,
          },
        ]}
      />
      <LabwareSearch onKeywordChange={setFilterCondition} deepWellThreshold={deepWellThreshold} />
      <div className="labware-list-content__scrollable-labwares block-scrollable">
        {Object.values(labwareList).map((labware, index) => (
          <DraggableLabware
            ref={(el) => {
              labwareRefs.current[index] = el;
            }}
            key={labware.LabwareID}
            itemID={labware.LabwareID}
            itemType={DragDropType.LIST_LABWARE}
            name={labware.name}
            imgPath={labware.image}
            onDrag={() => {
              setDraggedLabwareId(labware.LabwareID);
              setDraggedLabwareType(DragDropType.LIST_LABWARE);
            }}
            isDeepWell={labware.wellDepth > deepWellThreshold}
            id={index}
            isFavorite={labware?.isFavorite}
          />
        ))}
      </div>
    </LabwareList>
  );
}

LabwareListContainer.propTypes = {
  intl: PropTypes.object.isRequired,
  availableLabwareState: PropTypes.object.isRequired,
  deepWellThreshold: PropTypes.number.isRequired,
  filterParameter: PropTypes.any,
  setDraggedLabwareId: PropTypes.func,
  setDraggedLabwareType: PropTypes.func,
};

LabwareListContainer.defaultProps = {
  filterParameter: {},
  setDraggedLabwareId: () => {},
  setDraggedLabwareType: () => {},
};

function areEqual(prevProps, nextProps) {
  return (
    _.isEqual(prevProps.availableLabwareState, nextProps.availableLabwareState) &&
    _.isEqual(prevProps.deepWellThreshold, nextProps.deepWellThreshold) &&
    _.isEqual(prevProps.filterParameter, nextProps.filterParameter) &&
    _.isEqual(prevProps.setDraggedLabwareId, nextProps.setDraggedLabwareId)
  );
}

export default React.memo(connect(mapStateToProps, null)(withIntl(LabwareListContainer)), areEqual);
